// Re-platform dialog. Lets a user migrate the whole architecture between
// AWS / Azure / GCP. Costs 1 AI credit on metered plans (Free, Pro);
// unlimited on Premium/Team.
//
// The LLM call takes ~20-40s, so we show an animated progress bar and a
// running elapsed-seconds counter while we wait.

import { useEffect, useRef, useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { PROVIDER_LABELS, PROVIDER_ACCENTS } from "../../lib/archModel";

const PROVIDERS = ["aws", "azure", "gcp"];

export default function ReplatformDialog({
  open,
  onClose,
  currentProvider,
  inFlight,
  onSubmit,
}) {
  const [picked, setPicked] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPicked(null);
      setElapsed(0);
    }
  }, [open]);

  useEffect(() => {
    if (!inFlight) {
      startRef.current = null;
      setElapsed(0);
      return undefined;
    }
    startRef.current = Date.now();
    setElapsed(0);
    const t = setInterval(() => {
      if (startRef.current) {
        setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(t);
  }, [inFlight]);

  const available = PROVIDERS.filter((p) => p !== currentProvider);

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && !inFlight && onClose?.()}>
      <AlertDialogContent
        data-testid="replatform-dialog"
        style={{
          background: "var(--color-background-primary)",
          border: "1px solid var(--color-border-primary)",
          color: "var(--color-text-primary)",
          maxWidth: 520,
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Re-platform this architecture</AlertDialogTitle>
          <AlertDialogDescription>
            Migrate every node to its closest equivalent on the target cloud.
            Existing structural hierarchy is replaced and the action is recorded
            as a new version. Counts as one monthly architecture run on Pro or Team.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex items-center gap-3" style={{ margin: "8px 0 6px" }}>
          <span
            className="font-mono"
            style={{
              fontSize: 11,
              color: "var(--color-text-tertiary)",
              padding: "5px 10px",
              borderRadius: 9999,
              background: "var(--color-background-secondary)",
              border: "1px solid var(--color-border-primary)",
            }}
            data-testid="replatform-current"
          >
            From: {PROVIDER_LABELS[currentProvider] || currentProvider}
          </span>
          <ArrowRight size={14} color="var(--color-text-tertiary)" />
        </div>

        <div className="flex flex-col gap-2">
          {available.map((p) => {
            const isPicked = picked === p;
            return (
              <button
                key={p}
                type="button"
                disabled={inFlight}
                onClick={() => setPicked(p)}
                data-testid={`replatform-option-${p}`}
                className="motion-micro"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  width: "100%",
                  background: isPicked
                    ? "color-mix(in oklab, var(--color-accent) 12%, var(--color-background-secondary))"
                    : "var(--color-background-secondary)",
                  border: `1px solid ${
                    isPicked ? "var(--color-accent)" : "var(--color-border-primary)"
                  }`,
                  color: "var(--color-text-primary)",
                  fontSize: 13.5,
                  fontWeight: 500,
                  padding: "12px 14px",
                  borderRadius: 10,
                  cursor: inFlight ? "not-allowed" : "pointer",
                  textAlign: "left",
                }}
              >
                <span className="flex items-center gap-2">
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: 9999,
                      background: PROVIDER_ACCENTS[p] || "#64748B",
                    }}
                  />
                  {PROVIDER_LABELS[p]}
                </span>
                <span
                  className="font-mono"
                  style={{ fontSize: 10, color: "var(--color-text-tertiary)" }}
                >
                  {p.toUpperCase()}
                </span>
              </button>
            );
          })}
        </div>

        {inFlight && (
          <div
            data-testid="replatform-progress"
            style={{
              marginTop: 14,
              padding: "10px 12px",
              background: "var(--color-background-secondary)",
              border: "1px solid var(--color-border-primary)",
              borderRadius: 10,
            }}
          >
            <div className="flex items-center justify-between gap-2" style={{ marginBottom: 8 }}>
              <span
                className="inline-flex items-center gap-2"
                style={{ fontSize: 12, color: "var(--color-text-primary)" }}
              >
                <Loader2 size={13} className="animate-spin" color="var(--color-accent)" />
                Re-mapping {available.length > 0 && picked ? PROVIDER_LABELS[picked] : "services"}…
              </span>
              <span
                className="font-mono"
                style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}
              >
                {elapsed}s
              </span>
            </div>
            <div
              className="progress-indeterminate"
              style={{
                height: 4,
                borderRadius: 9999,
                background: "var(--color-background-tertiary)",
                overflow: "hidden",
              }}
            />
            <p
              style={{
                fontSize: 11,
                color: "var(--color-text-tertiary)",
                marginTop: 8,
                lineHeight: 1.4,
              }}
            >
              Typical time: 20–40 seconds. The new structure will replace this view
              and be saved as a new version.
            </p>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="replatform-cancel" disabled={inFlight}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="replatform-confirm"
            disabled={!picked || inFlight}
            onClick={(e) => {
              e.preventDefault();
              if (picked && !inFlight) onSubmit?.(picked);
            }}
            style={{
              background: picked && !inFlight ? "var(--color-accent)" : "var(--color-background-tertiary)",
              color: picked && !inFlight ? "#0a0a0a" : "var(--color-text-tertiary)",
              border: "none",
            }}
          >
            {inFlight ? "Working…" : "Re-platform"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
